import React, { ReactNode, useCallback, useMemo } from 'react';
import { ClientWebPage } from '@common/model/meta/clientWebPage';
import {
  DialogContextProvider,
  DialogContextType,
  DialogList,
  DialogListProps,
  DialogUserContextProvider,
  ICurrentUserProvider,
  coreDialogApi,
  dialogModel,
  useMessageResolver,
} from '@cp/entities/dialog';
import { useCurrentUser } from '@cp/entities/currentUser';
import { useRouter } from '@cp/shared/router';
import { SxProps } from '@cp/ds';
import { useClientWebResolver } from '@cp/entities/clientWebRoutes';
import { DialogsFilterType } from '@common/model/dialog/filter';
import { useDispatch, useSelector } from 'react-redux';
import { DialogEvents, DialogShowAllLink } from 'src/features/dialog';
import { useWithTrack } from 'src/shared/tracking';
import { MessageRenderer } from '../../dialog/ui/messageRenderer';
import { DialogListItem } from './dialogListItem';

export interface DialogListWidgetProps extends Pick<DialogListProps, 'headerBottom'> {
  title?: ReactNode;
  // TODO: implement
  filter?: DialogsFilterType;
  pagination?: number;
  sx?: SxProps;
  layout?: 'row';
}

export const DialogListWidget: React.FC<DialogListWidgetProps> = ({ title, pagination, layout, sx, headerBottom }) => {
  const currentUser = useCurrentUser();
  const dispatch = useDispatch();
  const router = useRouter();
  const resolver = useClientWebResolver();
  const dialogIds = useSelector(dialogModel.selectors.dialogsIdsSelector);
  const messagesPageUrl = useMemo(() => String(resolver.resolve({ page: ClientWebPage.ProfileMessages })), [resolver]);
  const isMessagesPage = messagesPageUrl === router.location.pathname;
  const page = 1; // pagination not used on client app
  const { data: dialogsList, isLoading } = coreDialogApi.useGetDialogsQuery({ page, pagination });
  const currentUserProvider: ICurrentUserProvider = useMemo(() => ({ getUser: () => currentUser }), [currentUser]);
  const withTrack = useWithTrack();

  React.useEffect(() => {
    if (!dialogsList) {
      return;
    }
    dispatch(dialogModel.actions.dialogsLoaded({ ...dialogsList, page }));
  }, [dialogsList, dispatch]);

  const onSelectDialog = useCallback(
    (dialogId: number) => {
      return router.navigate(resolver.resolve({ page: ClientWebPage.ProfileMessages, queryParams: { dialogId } }));
    },
    [resolver, router],
  );

  const listRootSx: SxProps | undefined = useMemo(() => {
    if (layout !== 'row') {
      return;
    }
    return {
      border: 'none',
      display: 'flex',
      flexDirection: 'row',
      gap: 1.5,
    };
  }, [layout]);

  const headerActions = useMemo(() => {
    if (isMessagesPage) {
      return null;
    }
    return <DialogShowAllLink href={messagesPageUrl} />;
  }, [isMessagesPage, messagesPageUrl]);

  const contextValue: DialogContextType = useMemo(
    () => ({
      useMessageResolver,
      MessageComponent: MessageRenderer,
    }),
    [],
  );

  return (
    <DialogUserContextProvider value={currentUserProvider}>
      <DialogContextProvider value={contextValue}>
        <DialogList
          headerActions={headerActions}
          headerBottom={headerBottom}
          isLoading={isLoading}
          listRootSx={listRootSx}
          sx={sx}
          title={title}
        >
          {dialogIds.map((dialogId) => (
            <DialogListItem
              currentUser={currentUser}
              dialogId={dialogId}
              key={dialogId}
              layout={layout}
              onSelect={withTrack(() => DialogEvents.DialogSelect(), onSelectDialog)}
            />
          ))}
        </DialogList>
      </DialogContextProvider>
    </DialogUserContextProvider>
  );
};
